import { getEntityRouteParam } from './entityIdentifier';

let lastBreadcrumbMeta = null;

/**
 * Classifies a native UI resource path so callers can decide whether to keep
 * or clear the remembered breadcrumb meta.
 * list: `/schemas`, schema: create/edit, object: records list, record: record show.
 */
export const classifyNativeUiResource = resource => {
  const path = normalizeResourcePath(resource);
  if (/^\/schemas\/[^/]+\/records\/[^/]+$/.test(path)) {
    return 'record';
  }
  if (/^\/schemas\/[^/]+\/records$/.test(path)) {
    return 'object';
  }
  if (path === '/schemas/create' || /^\/schemas\/edit\/[^/]+$/.test(path)) {
    return 'schema';
  }
  return 'list';
};

function normalizeResourcePath(resource) {
  if (!resource) {
    return '/schemas';
  }
  return String(resource).split('?')[0].replace(/\/+$/, '') || '/schemas';
}

/**
 * Schema id from the route: `/schemas/edit/:id`, `/schemas/:id/records` or
 * `/schemas/:id/records/:displayId`. Undefined for list and create routes.
 */
const getSchemaIdFromResource = resource => {
  const path = normalizeResourcePath(resource);
  const match = path.match(/^\/schemas\/edit\/([^/]+)$/) || path.match(/^\/schemas\/([^/]+)\/records(?:\/[^/]+)?$/);
  return match?.[1];
};

/**
 * Remembers object/record breadcrumb meta across in-iframe navigations.
 * List and create routes clear it. Edit/object/record emits that omit meta (e.g. while
 * entity data is still loading) reuse the last named object, but only when it belongs to the
 * schema in the route, so an early ROUTE_CHANGE cannot wipe out the host's breadcrumbs nor
 * show another object's name.
 */
export const resolveRouteChangeMeta = (resource, meta) => {
  const page = classifyNativeUiResource(resource);
  const schemaId = getSchemaIdFromResource(resource);
  if (page === 'list' || !schemaId) {
    lastBreadcrumbMeta = null;
    return null;
  }
  if (meta?.object) {
    if (meta.object.name) {
      lastBreadcrumbMeta = meta;
    }
    return meta;
  }
  if (lastBreadcrumbMeta && String(lastBreadcrumbMeta.object?.id) === String(schemaId)) {
    // record meta is route specific (the host falls back to the display id in the resource)
    return { object: lastBreadcrumbMeta.object };
  }
  return null;
};

export const resetLastRouteChangeMeta = () => {
  lastBreadcrumbMeta = null;
};

/**
 * Builds ROUTE_CHANGE meta for the host breadcrumb bar.
 */
export function buildRouteChangeMeta({ entity, record, displayId, routeParam } = {}) {
  if (!entity?.name && !entity?.title) {
    return null;
  }

  const param = routeParam ?? getEntityRouteParam(entity);
  if (!param) {
    return null;
  }

  const objectPath = `/schemas/${param}/records`;
  const meta = {
    object: {
      name: entity.title || entity.name,
      id: String(param),
      path: objectPath,
    },
  };

  const recordId = displayId ?? record?.display_id ?? record?.id;
  if (recordId != null && recordId !== '') {
    meta.record = {
      id: String(recordId),
      name: String(recordId),
      path: `${objectPath}/${recordId}`,
    };
  }

  return meta;
}
